import { http } from './http';
import { downloadBlob } from '@/utils/blob';

/**
 * Family Office viewer API (docs/FAMILY_OFFICE_AND_DELEGATED_OTP.md §4.3). Read-only: balances,
 * per-member transactions with a period filter, and TSV exports. All access is grant-gated server-side
 * — the endpoints only ever return members the caller has a grant over.
 */

export interface CardBalance {
  cardId: string;
  /** Последние четыре цифры карты — по ним человек её и узнаёт. Null → показываем кусок id. */
  last4: string | null;
  amount: number | string | null;
  currency: string | null;
  asOf: string | null;
  available: boolean;
}

/** Круг участника — по нему экран раскладывает людей на блоки «Семья / Дом / Бизнес». */
export type Circle = 'FAMILY' | 'HOUSEHOLD' | 'BUSINESS';

export interface MemberBalances {
  customerId: string;
  /** Masked display name (first name + last initial), resolved server-side from Customer. Null → fall back to id. */
  name: string | null;
  /** Null — оператор ещё не разметил: показываем такого в конце, без заголовка блока. */
  circle: Circle | null;
  /**
   * Подпись оператора: «Повар», «Финдиректор». Null → под именем не пишем ничего. Фоллбэка на
   * название круга нет намеренно — человек и так стоит в блоке этого круга.
   */
  roleLabel: string | null;
  balances: CardBalance[];
}

export interface FamilyOverview {
  members: MemberBalances[];
}

export interface FamilyTx {
  occurredAt: string | null;
  accountCustomerId: string;
  rail: string;
  type: string;
  status: string;
  amountNative: number | string | null;
  currencyNative: string | null;
  amountFiat: number | string | null;
  currencyFiat: string | null;
  merchant: string | null;
  merchantCountry: string | null;
  mccCategory: string | null;
  reference: string | null;
  description: string | null;
}

/** Optional ISO date (yyyy-MM-dd) window. Server defaults to the current month when omitted. */
function periodParams(from?: string, to?: string): Record<string, string> {
  const p: Record<string, string> = {};
  if (from) p.from = from;
  if (to) p.to = to;
  return p;
}

/**
 * Как назвать круг клиента. Считается сервером из СОСТАВА кругов участников, не хранится и не
 * настраивается: отдельный тип группы убран как вторая ось с теми же словами.
 */
export type FamilyGroupLabel = 'FAMILY' | 'COMPANY' | 'MIXED';

export interface FamilyAccess {
  hasAccess: boolean;
  canExport: boolean;
  /** Право VIEW_TX. Без него экран участника отдаёт 403 — переход туда предлагать нельзя. */
  canViewTransactions: boolean;
  groupLabel: FamilyGroupLabel;
}

export const familyApi = {
  /** Cheap probe (no balance fetch) — drives whether the Family Office nav entry is shown. */
  access(): Promise<FamilyAccess> {
    return http.get<FamilyAccess>('/v1/family/access').then((r) => r.data);
  },

  overview(): Promise<FamilyOverview> {
    return http.get<FamilyOverview>('/v1/family/overview').then((r) => r.data);
  },

  memberTransactions(customerId: string, from?: string, to?: string): Promise<FamilyTx[]> {
    return http
      .get<FamilyTx[]>(`/v1/family/members/${customerId}/transactions`, { params: periodParams(from, to) })
      .then((r) => r.data);
  },

  downloadMemberTsv(customerId: string, from?: string, to?: string): Promise<void> {
    return downloadBlob(`/v1/family/members/${customerId}/transactions.tsv`, periodParams(from, to),
      `tx_${customerId}_${from ?? 'start'}_${to ?? 'now'}.tsv`);
  },

  downloadGroupTsv(from?: string, to?: string): Promise<void> {
    return downloadBlob('/v1/family/transactions.tsv', periodParams(from, to),
      `tx_group_${from ?? 'start'}_${to ?? 'now'}.tsv`);
  },

  downloadBalancesTsv(): Promise<void> {
    return downloadBlob('/v1/family/balances.tsv', {}, 'balances_group.tsv');
  },
};
